/**
 * Guarantees every workspace has its default "Tasks" list, including
 * accounts that predate the tasks feature (unlike seedOnboarding, this runs
 * for non-empty workspaces too). Ids are client-minted (invariant 3), so
 * seeding is client-side; the sync server's REST path lazily seeds the same
 * way for spaces no client has booted.
 *
 * Two devices booting simultaneously can each seed one; the deterministic
 * winner is the smallest createdAt (tiebreak smallest id) and everyone else
 * clears the losers' flags, so healing converges.
 */
import { docName, resolveDefaultList } from "@hydra-notes/shared";
import { getData } from "./store";

const SYNC_WAIT_MS = 8_000;

export async function ensureDefaultTaskList(): Promise<void> {
  const ctx = getData();
  await ctx.workspace.whenLoaded;
  if (ctx.destroyed) return;
  if (ctx.sync) {
    // A fresh device's IndexedDB loads empty BEFORE the server sync delivers
    // the existing list; seeding in that window makes a duplicate. Wait for
    // the first server sync (attach is idempotent, this reuses the existing
    // attachment), falling back to a timeout so an offline boot still seeds;
    // the heal pass below converges any duplicate the fallback ever causes.
    const attachment = ctx.sync.attach(docName.workspace(ctx.workspaceId), ctx.workspace.doc);
    await Promise.race([attachment.whenSynced, new Promise((r) => setTimeout(r, SYNC_WAIT_MS))]);
    if (ctx.destroyed) return;
  }
  const items = ctx.workspace.getSnapshot();
  const flagged = items.filter((i) => i.type === "tasklist" && i.builtin === "tasks" && !i.trashedAt);
  if (flagged.length === 0) {
    const id = ctx.workspace.createItem("tasklist", null, "Tasks");
    ctx.workspace.setBuiltin(id, "tasks");
    return;
  }
  if (flagged.length > 1) {
    const winner = resolveDefaultList(items);
    for (const l of flagged) {
      if (l.id !== winner?.id) ctx.workspace.setBuiltin(l.id, undefined);
    }
  }
}
